export type TRobloxRankCategory = 'popular' | 'genres' | 'earnings' | 'featured';

export interface IRobloxRankSourceOption {
  label: string;
  value: string;
}

export interface IRobloxRankSourceItem {
  value: string;
  labelKey: string;
  descriptionKey: string;
}

export interface IRobloxRankCategoryItem {
  value: TRobloxRankCategory;
  labelKey: string;
  sources: IRobloxRankSourceItem[];
}

/** API sources 枚举，value 与请求/响应 source 字段一致 */
export const ROBLOX_RANK_SOURCE_OPTIONS: IRobloxRankSourceOption[] = [
  { label: 'Fun with Friends', value: 'Fun with Friends' },
  { label: 'Learn & Explore', value: 'Learn & Explore' },
  { label: 'More When You Subscribe', value: 'More When You Subscribe' },
  { label: 'Most Popular', value: 'Most Popular' },
  { label: 'Top Earning', value: 'Top Earning' },
  { label: 'Top Paid Access', value: 'Top Paid Access' },
  { label: 'Top Playing Now', value: 'Top Playing Now' },
  { label: 'Top Rated', value: 'Top Rated' },
  { label: 'Top Revisited', value: 'Top Revisited' },
  { label: 'Top Trending', value: 'Top Trending' },
  { label: 'Trending Music Experiences', value: 'Trending Music Experiences' },
  { label: 'Trending in Action', value: 'Trending in Action' },
  { label: 'Trending in Adventure', value: 'Trending in Adventure' },
  { label: 'Trending in Entertainment', value: 'Trending in Entertainment' },
  { label: 'Trending in Obby & Platformer', value: 'Trending in Obby & Platformer' },
  { label: 'Trending in Party & Casual', value: 'Trending in Party & Casual' },
  { label: 'Trending in Puzzle', value: 'Trending in Puzzle' },
  { label: 'Trending in RPG', value: 'Trending in RPG' },
  { label: 'Trending in Roleplay & Avatar Sim', value: 'Trending in Roleplay & Avatar Sim' },
  { label: 'Trending in Shooter', value: 'Trending in Shooter' },
  { label: 'Trending in Shopping', value: 'Trending in Shopping' },
  { label: 'Trending in Simulation', value: 'Trending in Simulation' },
  { label: 'Trending in Sports & Racing', value: 'Trending in Sports & Racing' },
  { label: 'Trending in Strategy', value: 'Trending in Strategy' },
  { label: 'Trending in Survival', value: 'Trending in Survival' },
  { label: 'Up-and-Coming', value: 'Up-and-Coming' },
];

export const ROBLOX_GAME_RANK_DEFAULT_CATEGORY: TRobloxRankCategory = 'popular';
export const ROBLOX_GAME_RANK_DEFAULT_SOURCE = 'Most Popular';

const buildSource = (value: string, keySuffix: string): IRobloxRankSourceItem => ({
  value,
  labelKey: `dbi_roblox_rank_source_${keySuffix}`,
  descriptionKey: `dbi_roblox_rank_source_${keySuffix}_desc`,
});

export const ROBLOX_GAME_RANK_CATEGORIES: IRobloxRankCategoryItem[] = [
  {
    value: 'popular',
    labelKey: 'dbi_roblox_rank_category_popular',
    sources: [
      buildSource('Most Popular', 'most_popular'),
      buildSource('Top Playing Now', 'top_playing_now'),
      buildSource('Top Trending', 'top_trending'),
      buildSource('Up-and-Coming', 'up_and_coming'),
      buildSource('Fun with Friends', 'fun_with_friends'),
    ],
  },
  {
    value: 'genres',
    labelKey: 'dbi_roblox_rank_category_genres',
    sources: [
      buildSource('Trending Music Experiences', 'trending_music_experiences'),
      buildSource('Trending in RPG', 'trending_in_rpg'),
      buildSource('Trending in Sports & Racing', 'trending_in_sports_racing'),
      buildSource('Trending in Shooter', 'trending_in_shooter'),
      buildSource('Trending in Action', 'trending_in_action'),
      buildSource('Trending in Adventure', 'trending_in_adventure'),
      buildSource('Trending in Entertainment', 'trending_in_entertainment'),
      buildSource('Trending in Obby & Platformer', 'trending_in_obby_platformer'),
      buildSource('Trending in Party & Casual', 'trending_in_party_casual'),
      buildSource('Trending in Puzzle', 'trending_in_puzzle'),
      buildSource('Trending in Roleplay & Avatar Sim', 'trending_in_roleplay_avatar_sim'),
      buildSource('Trending in Shopping', 'trending_in_shopping'),
      buildSource('Trending in Simulation', 'trending_in_simulation'),
      buildSource('Trending in Strategy', 'trending_in_strategy'),
      buildSource('Trending in Survival', 'trending_in_survival'),
    ],
  },
  {
    value: 'earnings',
    labelKey: 'dbi_roblox_rank_category_earnings',
    sources: [
      buildSource('Top Earning', 'top_earning'),
      buildSource('Top Paid Access', 'top_paid_access'),
      buildSource('More When You Subscribe', 'more_when_you_subscribe'),
    ],
  },
  {
    value: 'featured',
    labelKey: 'dbi_roblox_rank_category_featured',
    sources: [
      buildSource('Top Rated', 'top_rated'),
      buildSource('Top Revisited', 'top_revisited'),
      buildSource('Learn & Explore', 'learn_and_explore'),
    ],
  },
];

const ROBLOX_RANK_SOURCE_ITEM_MAP = ROBLOX_GAME_RANK_CATEGORIES.reduce<
  Record<string, IRobloxRankSourceItem>
>((acc, category) => {
  category.sources.forEach((source) => {
    acc[source.value] = source;
  });
  return acc;
}, {});

export const getRobloxRankSourcesByCategory = (
  category: TRobloxRankCategory,
): IRobloxRankSourceItem[] => {
  return ROBLOX_GAME_RANK_CATEGORIES.find((item) => item.value === category)?.sources || [];
};

export const getDefaultSourceByCategory = (category: TRobloxRankCategory): string => {
  return getRobloxRankSourcesByCategory(category)[0]?.value || ROBLOX_GAME_RANK_DEFAULT_SOURCE;
};

export const getRobloxRankSourceItemByValue = (
  sourceValue?: string,
): IRobloxRankSourceItem | undefined => {
  if (!sourceValue) {
    return undefined;
  }

  return ROBLOX_RANK_SOURCE_ITEM_MAP[sourceValue];
};

export const getRobloxRankSourceDisplayLabel = (
  sourceValue: string | undefined,
  formatMessage: (descriptor: { id: string }) => string,
  fallbackLabelKey = 'dbi_roblox_game_rank_section_title',
): string => {
  const sourceItem = getRobloxRankSourceItemByValue(sourceValue);

  if (sourceItem?.labelKey) {
    return formatMessage({ id: sourceItem.labelKey });
  }

  if (sourceValue) {
    return sourceValue;
  }

  return formatMessage({ id: fallbackLabelKey });
};

export const getRobloxRankCategoryBySource = (
  sourceValue?: string,
): TRobloxRankCategory | undefined => {
  if (!sourceValue) {
    return undefined;
  }

  return ROBLOX_GAME_RANK_CATEGORIES.find((category) =>
    category.sources.some((source) => source.value === sourceValue),
  )?.value;
};

export const isValidRobloxRankSource = (sourceValue?: string): boolean => {
  return Boolean(sourceValue && ROBLOX_RANK_SOURCE_ITEM_MAP[sourceValue]);
};
